/**
 * JobDetailsSection — the job-details save (PATCH
 * /api/admin/events/{id}/workspace) routes through
 * event-workspace-api.patchWorkspace, auto-persisted via useAutosave (#320
 * pattern): debounced on edit, flushed on unmount, force-saved through
 * `savables` before job-summary generation and transport writes.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { SectionCard } from "../ui/SectionCard";
import { Tooltip } from "../ui/Tooltip";
import { Button } from "../ui/Button";
import { ConfirmModal } from "../ui/ConfirmModal";
import { cx } from "@shared/cx";
import forms from "../ui/field.module.css";
import srOnly from "@shared/a11y.module.css";
import { TimeInput } from "../ui/TimeInput";
import { Select } from "../ui/Select";
import { DateInputNZ } from "../ui/DateInputNZ";
import {
  ResponsiveTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../ui/ResponsiveTable";
import { PlusIcon, XIcon } from "../ui/icons";
import { formatTimeDisplay, getEventDayCount } from "../../lib/format-utils";
import {
  useRegisterSavable,
  type SavableSections,
} from "../../lib/use-savable-sections";
import {
  usePublishSectionDirty,
  type DirtySectionsRegistry,
} from "../../lib/use-dirty-sections";
import {
  patchWorkspace,
  type DayScheduleKind,
  type DaySchedulePayload,
  type JobDetailsPatch,
} from "../../lib/event-workspace-api";
import { useAutosave } from "../../lib/use-autosave";
import type {
  DayVehicleTimes,
  VehicleDayTimes,
} from "../../lib/use-vehicle-day-times";
import {
  appendDay,
  buildDefaultDaySchedules,
  buildJobDetailsPatch,
  renumberByDate,
  type QuotedLabour,
} from "../../lib/job-details-payload";
import cell from "@/components/ui/cell-input.module.css";
import styles from "./JobDetailsSection.module.css";

// The vehicle a Contractor drives is a Crew field (migration 120): a Booking
// plans a whole set of vehicles, so each driver picks theirs on their own row.

export interface DaySchedule extends DaySchedulePayload {
  id?: string;
}

/**
 * The day's vehicle out/back pair, on the schedule row rather than in a grid
 * of its own: every planned Vehicle on a job leaves and returns together, so the
 * times belong beside the day's other times. Commits on blur like them, and
 * fans out to every planned Vehicle (see use-vehicle-day-times).
 */
function VehicleTimeCells({
  dayLabel,
  times,
  onCommit,
  disabled,
  timeCell,
}: {
  dayLabel: string;
  times: DayVehicleTimes;
  onCommit: (next: DayVehicleTimes) => void;
  disabled: boolean;
  timeCell: string;
}) {
  const [pair, setPair] = useState(times);
  // Ref mirror written in the same event that types or picks a value: the
  // commit fires from a deferred focusout, and TimeInput force-blurs its input
  // in the click that calls onChange, so state has not flushed yet.
  const latest = useRef(times);
  const committed = useRef(times);
  useEffect(() => {
    setPair(times);
    latest.current = times;
    committed.current = times;
  }, [times]);

  const change = (next: DayVehicleTimes) => {
    latest.current = next;
    setPair(next);
  };

  // Deferred so moving between the two fields does not commit a half-edited
  // pair — both are saved together on each blur.
  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    const from = e.currentTarget;
    window.setTimeout(() => {
      if (from.contains(document.activeElement)) return;
      const next = latest.current;
      if (
        next.out === committed.current.out &&
        next.back === committed.current.back
      )
        return;
      committed.current = next;
      onCommit(next);
    }, 0);
  };

  return (
    <>
      <TableCell label="Vehicles Out" span={1} padding="tight">
        <div onBlur={handleBlur}>
          <TimeInput
            ariaLabel={`${dayLabel} Vehicles Out`}
            value={pair.out}
            onChange={(value) => change({ ...latest.current, out: value })}
            inputStyles={timeCell}
            disabled={disabled}
          />
        </div>
      </TableCell>
      <TableCell label="Vehicles Back" span={1} padding="tight">
        <div onBlur={handleBlur}>
          <TimeInput
            ariaLabel={`${dayLabel} Vehicles Back`}
            value={pair.back}
            onChange={(value) => change({ ...latest.current, back: value })}
            inputStyles={timeCell}
            disabled={disabled}
          />
        </div>
      </TableCell>
    </>
  );
}

interface JobDetailsSectionProps {
  bookingId: string;
  callTime: string;
  packOutTime: string;
  siteAvailableFrom: string;
  callOutNotes: string;
  eventDate: string;
  eventEndDate: string | null;
  eventTime: string | null;
  daySchedules?: DaySchedule[];
  /** The planned Vehicles' out/back pair per day — one of this table's columns. */
  vehicleDayTimes: VehicleDayTimes;
  /**
   * Quoted labour hours per phase (#181). Seeds setup/packout schedule rows
   * (and a travel note) when the booking has no saved schedule yet, so the
   * quote's labour breakdown lands in the schedule instead of a second one.
   */
  quotedLabour?: QuotedLabour | null;
  state: "active" | "complete" | "future";
  open?: boolean;
  onToggle?: () => void;
  sectionRef?: React.RefObject<HTMLDivElement | null>;
  children?: ReactNode;
  onGenerateJobSummary: () => Promise<void>;
  /**
   * Refetch workspace data after a save, so a stage-tab switch (which
   * unmounts this section) remounts it seeded from what was just persisted
   * rather than the pre-save read.
   */
  onRefresh: () => Promise<void>;
  contractorsNotifiedAt: string | null;
  generatingJobSummary?: boolean;
  /** Job-summary generation timestamp — flips the button to "Re-generate". */
  jobSummaryGeneratedAt?: string | null;
  assignmentCount: number;
  /** Workspace savable-sections registry — this section's pending edits are flushed via saveAll() before generation. */
  savables?: SavableSections;
  /** Dirty registry — publishes this section's unsaved-edits flag (a job input). */
  dirtySections?: DirtySectionsRegistry;
}

export function JobDetailsSection({
  bookingId,
  callTime: initialCallTime,
  packOutTime: initialPackOutTime,
  siteAvailableFrom: initialSiteAvailable,
  callOutNotes: initialNotes,
  eventDate,
  eventEndDate,
  eventTime,
  daySchedules: initialDaySchedules,
  vehicleDayTimes,
  quotedLabour,
  state,
  open,
  onToggle,
  sectionRef,
  children,
  onGenerateJobSummary,
  onRefresh,
  contractorsNotifiedAt,
  generatingJobSummary = false,
  jobSummaryGeneratedAt = null,
  assignmentCount,
  savables,
  dirtySections,
}: JobDetailsSectionProps) {
  const [showRegenConfirm, setShowRegenConfirm] = useState(false);
  const [callOutNotes, setCallOutNotes] = useState(initialNotes);

  const dayCount = getEventDayCount(eventDate, eventEndDate);

  // The table is the only schedule editor at every day count, so a booking that
  // has never been edited still opens with a row per event day — seeded from
  // the flat booking columns those rows have now replaced. Site Open / Call /
  // Pack Out by used to be a separate three-input grid above this table, which
  // meant a one-day job and a multi-day job were edited in two different
  // shapes, and the same three times existed in two places at once.
  const [schedules, setSchedules] = useState<DaySchedule[]>(() => {
    if (initialDaySchedules && initialDaySchedules.length > 0)
      return initialDaySchedules;
    if (!eventDate) return [];
    return buildDefaultDaySchedules({
      eventDate,
      dayCount,
      eventTime,
      siteAvailableFrom: initialSiteAvailable,
      callTime: initialCallTime,
      packOutTime: initialPackOutTime,
      quotedLabour,
    });
  });

  const updateScheduleField = (
    dayIndex: number,
    field: keyof DaySchedule,
    value: string,
  ) => {
    setSchedules((prev) =>
      prev.map((s, i) => (i === dayIndex ? { ...s, [field]: value } : s)),
    );
  };

  // Redating a row can reorder it, so the set is re-sorted and renumbered.
  const updateScheduleDate = (dayIndex: number, value: string) => {
    if (!value) return;
    setSchedules((prev) =>
      renumberByDate(
        prev.map((s, i) =>
          i === dayIndex ? { ...s, schedule_date: value } : s,
        ),
      ),
    );
  };

  const addDay = () => setSchedules((prev) => appendDay(prev, eventDate));

  const removeScheduleRow = (dayIndex: number) => {
    setSchedules((prev) =>
      renumberByDate(prev.filter((_, i) => i !== dayIndex)),
    );
  };

  // bookings.call_time / pack_out_time / site_available_from no longer have
  // inputs of their own — the table's Event row is where those times are typed
  // now. They still feed the contractor email and the job sheet CALL TIME box,
  // so they are derived from that row rather than dropped.
  const eventRow = schedules.find((s) => s.kind === "event") ?? schedules[0];
  const callTime = eventRow?.call_time ?? "";
  const packOutTime = eventRow?.pack_out ?? "";
  const siteAvailableFrom = eventRow?.site_open ?? "";

  const formatDayLabel = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-NZ", { weekday: "short", day: "numeric" });
  };

  // One payload feeds the autosave value, the savable flush, and the save
  // itself, so "dirty" is exactly "the patch the server would receive has
  // changed".
  const patch = buildJobDetailsPatch({
    callTime,
    packOutTime,
    siteAvailableFrom,
    callOutNotes,
    schedules,
  });

  // Debounced auto-save of job-detail edits — the same #320 treatment the
  // gear editor got. Edits used to live only in React state until
  // savables.saveAll() persisted them (job-summary generation, transport
  // writes); switching the stage tab unmounted the section first and silently
  // dropped every keystroke. Now a change saves ~1s after typing stops and a
  // pending edit is flushed on unmount. The refetch reconciles ws.data with
  // what was persisted, so a remount re-seeds from the saved state instead of
  // the stale pre-save read.
  const { saveNow, dirty } = useAutosave<JobDetailsPatch>({
    value: patch,
    enabled: true,
    save: async (value) => {
      await patchWorkspace(bookingId, "job_details", value);
      // The refetch resolves BEFORE the autosave baseline advances, so the
      // dirty flag below spans the whole save round-trip (summary-staleness.ts).
      await onRefresh();
    },
  });

  // Savable registration: Generate Job Summary and transport writes flush
  // through the autosave's serialised queue, so a forced save can never race
  // (or be overtaken by) a debounced one.
  useRegisterSavable(savables, "job-details", () => saveNow());

  // This section's staleness contribution is one boolean: unsaved edits exist.
  // "Dirty" is exactly "the patch the server would receive has changed" — the
  // autosave fingerprint over `patch` — and every field of that patch is a
  // job-summary input, so dirty → job artifact stale is exact.
  usePublishSectionDirty(dirtySections, "jobDetails", dirty);

  const summary =
    [
      callTime && `Call: ${formatTimeDisplay(callTime)}`,
      packOutTime && `Pack Out by: ${formatTimeDisplay(packOutTime)}`,
    ]
      .filter(Boolean)
      .join(" · ") || "Not filled in yet";

  // The borderless-until-hover cell, shared with the gear editor and the
  // crew table rather than restated here.
  const timeCell = cell.cell;

  return (
    <SectionCard
      title="Job Details"
      collapsible
      open={open}
      onToggle={onToggle}
      sectionRef={sectionRef}
      state={state}
      summary={summary}
    >
      <div className={styles.body}>
        <div>
          {/* Same treatment as the Vehicles and Crew blocks below it — these
              are the three headings inside Job Details. */}
          <h3 className={styles.groupHeading}>
            Schedule ({schedules.length}{" "}
            {schedules.length === 1 ? "day" : "days"})
          </h3>
          <ResponsiveTable>
            <TableHead>
              <TableHeaderCell padding="header" width="36">
                Day
              </TableHeaderCell>
              <TableHeaderCell padding="header" width="28">
                <Tooltip content="Setup and pack-out can fall on their own day, before or after the event.">
                  Phase
                </Tooltip>
              </TableHeaderCell>
              <TableHeaderCell padding="header" width="28">
                <Tooltip content="The earliest time crew can access the venue or site.">
                  Site Open
                </Tooltip>
              </TableHeaderCell>
              <TableHeaderCell padding="header" width="28">
                Call
              </TableHeaderCell>
              <TableHeaderCell padding="header" width="28">
                Event Start
              </TableHeaderCell>
              <TableHeaderCell padding="header" width="28">
                Event End
              </TableHeaderCell>
              <TableHeaderCell padding="header" width="28">
                <Tooltip content="The latest planned load-out completion time.">
                  Pack Out by
                </Tooltip>
              </TableHeaderCell>
              <TableHeaderCell padding="header" width="28">
                <Tooltip content="When this phase must be complete — stage handed over, or site cleared.">
                  Ready By
                </Tooltip>
              </TableHeaderCell>
              <TableHeaderCell padding="header" width="28">
                <Tooltip content="When the vehicles leave the warehouse that day — every planned vehicle shares it.">
                  Vehicles Out
                </Tooltip>
              </TableHeaderCell>
              <TableHeaderCell padding="header" width="28">
                <Tooltip content="When the vehicles are due back that day — blank commits them for the whole day.">
                  Vehicles Back
                </Tooltip>
              </TableHeaderCell>
              <TableHeaderCell padding="header">Notes</TableHeaderCell>
              <TableHeaderCell padding="header" width="8">
                <span className={srOnly.srOnly}>Remove</span>
              </TableHeaderCell>
            </TableHead>
            <TableBody>
              {schedules.map((day, i) => (
                // Mobile: one card per day. Two columns, not the gear
                // editor's four — date/phase/notes take a full row and the
                // six times pair off, which keeps a ten-column schedule
                // legible without stacking ten labels vertically.
                <TableRow key={day.day_number} columns={2}>
                  <TableCell
                    label="Day"
                    // cornerGap reserves room for the corner delete button,
                    // absolutely positioned over the card's top-right.
                    padding="tight"
                    cornerGap="sm"
                    className={styles.nowrap}
                  >
                    <DateInputNZ
                      ariaLabel={`Day ${day.day_number} date`}
                      value={day.schedule_date}
                      onChange={(iso) => updateScheduleDate(i, iso)}
                      inputStyles={timeCell}
                    />
                  </TableCell>
                  <TableCell label="Phase" padding="tight">
                    <select
                      aria-label={`${formatDayLabel(day.schedule_date)} Phase`}
                      value={day.kind}
                      onChange={(e) =>
                        updateScheduleField(
                          i,
                          "kind",
                          e.target.value as DayScheduleKind,
                        )
                      }
                      className={cx(timeCell, styles.pointer)}
                    >
                      <option value="setup">Setup</option>
                      <option value="event">Event</option>
                      <option value="packout">Pack out</option>
                    </select>
                  </TableCell>
                  <TableCell label="Site Open" span={1} padding="tight">
                    <TimeInput
                      ariaLabel={`${formatDayLabel(day.schedule_date)} Site Open`}
                      value={day.site_open || ""}
                      onChange={(value) =>
                        updateScheduleField(i, "site_open", value)
                      }
                      inputStyles={timeCell}
                    />
                  </TableCell>
                  <TableCell label="Call" span={1} padding="tight">
                    <TimeInput
                      ariaLabel={`${formatDayLabel(day.schedule_date)} Call`}
                      value={day.call_time || ""}
                      onChange={(value) =>
                        updateScheduleField(i, "call_time", value)
                      }
                      inputStyles={timeCell}
                    />
                  </TableCell>
                  <TableCell label="Event Start" span={1} padding="tight">
                    <TimeInput
                      ariaLabel={`${formatDayLabel(day.schedule_date)} Event Start`}
                      value={day.event_start || ""}
                      onChange={(value) =>
                        updateScheduleField(i, "event_start", value)
                      }
                      inputStyles={timeCell}
                    />
                  </TableCell>
                  <TableCell label="Event End" span={1} padding="tight">
                    <TimeInput
                      ariaLabel={`${formatDayLabel(day.schedule_date)} Event End`}
                      value={day.event_end || ""}
                      onChange={(value) =>
                        updateScheduleField(i, "event_end", value)
                      }
                      inputStyles={timeCell}
                    />
                  </TableCell>
                  <TableCell label="Pack Out by" span={1} padding="tight">
                    <TimeInput
                      ariaLabel={`${formatDayLabel(day.schedule_date)} Pack Out by`}
                      value={day.pack_out || ""}
                      onChange={(value) =>
                        updateScheduleField(i, "pack_out", value)
                      }
                      inputStyles={timeCell}
                    />
                  </TableCell>
                  <TableCell label="Ready By" span={1} padding="tight">
                    <TimeInput
                      ariaLabel={`${formatDayLabel(day.schedule_date)} Ready By`}
                      value={day.ready_by || ""}
                      onChange={(value) =>
                        updateScheduleField(i, "ready_by", value)
                      }
                      inputStyles={timeCell}
                    />
                  </TableCell>
                  <VehicleTimeCells
                    dayLabel={formatDayLabel(day.schedule_date)}
                    times={vehicleDayTimes.timesFor(day.schedule_date)}
                    onCommit={(next) =>
                      vehicleDayTimes.commit(day.schedule_date, next)
                    }
                    // Nothing to time until a Vehicle is planned — the pair is
                    // stored per Vehicle, so a value typed now has nowhere to
                    // land and would vanish on the next read.
                    disabled={!vehicleDayTimes.planned}
                    timeCell={timeCell}
                  />
                  <TableCell label="Notes" padding="tight">
                    <input
                      type="text"
                      aria-label={`${formatDayLabel(day.schedule_date)} Notes`}
                      value={day.notes || ""}
                      onChange={(e) =>
                        updateScheduleField(i, "notes", e.target.value)
                      }
                      className={timeCell}
                    />
                  </TableCell>
                  <TableCell corner padding="tight">
                    <button
                      type="button"
                      aria-label={`Remove ${formatDayLabel(day.schedule_date)}`}
                      onClick={() => removeScheduleRow(i)}
                      className={styles.remove}
                    >
                      <XIcon className={styles.icon} />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </ResponsiveTable>
          {/* Same affordance as the gear editor's "Add gear" — ghost button,
              plus icon, sitting directly under the table it appends to. */}
          <div className={styles.addRow}>
            <Button
              variant="ghost"
              size="sm"
              onClick={addDay}
              disabled={!eventDate}
            >
              <span className={styles.addLabel}>
                <PlusIcon className={styles.icon} />
                Add day
              </span>
            </Button>
          </div>
        </div>

        <div>
          <label className={forms.label}>
            Shared Callout Notes{" "}
            <span className={forms.optional}>(optional)</span>
          </label>
          <textarea
            value={callOutNotes}
            onChange={(e) => setCallOutNotes(e.target.value)}
            rows={3}
            className={cx(forms.input, styles.resizable)}
          />
          <p className={styles.hint}>
            Shown to every assigned worker, including labour briefs.
          </p>
        </div>
      </div>
      {children}

      <div className={styles.footer}>
        <Button
          onClick={() => {
            if (contractorsNotifiedAt) {
              setShowRegenConfirm(true);
            } else {
              void onGenerateJobSummary();
            }
          }}
          loading={generatingJobSummary}
          disabled={assignmentCount === 0}
          className={styles.fullWidth}
        >
          {jobSummaryGeneratedAt
            ? "Re-generate Job Summary"
            : "Generate Job Summary"}
        </Button>
      </div>

      <ConfirmModal
        open={showRegenConfirm}
        title="Regenerate Job Summary"
        message="Regenerating updates the admin preview only. Already-notified contractors keep their existing job sheets until you click Notify again to push the new version."
        confirmLabel="Regenerate"
        confirmVariant="primary"
        loading={generatingJobSummary}
        onConfirm={() => {
          setShowRegenConfirm(false);
          void onGenerateJobSummary();
        }}
        onCancel={() => setShowRegenConfirm(false)}
      />
    </SectionCard>
  );
}
